/**
 * The Moonlight wall: raised ONLY when a player presses a start control with
 * too little Moonlight. Never on joining someone else's room, never in the
 * tutorial, never for a match already under way.
 *
 * THE HARD RULE, stated rather than implied because this codebase has twice
 * shipped a readiness gate that deleted the control it was gating:
 *
 *   THE LOBBY PLAY BUTTONS ARE NEVER REMOVED, HIDDEN OR DISABLED.
 *
 * They stay at full brightness and stay pressable; pressing one opens this
 * sheet instead of requesting a match. The button is the subject, not the
 * obstacle: it waits, present and enabled, under the sheet, and closing the
 * sheet hands the keyboard straight back to it.
 *
 * THE SHEET IS ITS OWN LAYER, ABOVE WHATEVER HOLDS THE BUTTON. It used to
 * mount beside the button that summoned it, which on the old lobby put it
 * under the play row. The hub moved Start Adventure into the Adventure sheet,
 * a refused start never leaves the lobby, so that sheet is open when this
 * rises — and mounting beside the button put the wall INSIDE the sheet's
 * scrolling card: cut off at the card's edge, over Start Adventure, its one
 * action below the fold, its × restyled by the sheet's button rule. So it is
 * a modal dialog on a layer of its own now, the way every dialog opened from
 * a hub sheet is (the summoner picker, the invitation, the store): the sheet
 * yields the keyboard to it while the focus is inside it.
 *
 * Four contents and exactly ONE action, never two stacked. Nothing here says
 * "cannot", "unavailable" or "needed", there is no countdown, no tier table,
 * no feature grid — and the store is never auto-opened. The store owns
 * selling; this sheet owns explaining.
 */

const MOONLIGHT_WALL_ID = 'moonlight-wall';
const MOONLIGHT_WALL_LAYER_ID = 'moonlight-wall-layer';

/* The button the open wall answers, so closing it can give the keyboard back. */
let returnFocusTo = null;

function removeExisting() {
  const layer = document.getElementById(MOONLIGHT_WALL_LAYER_ID);
  const wall = document.getElementById(MOONLIGHT_WALL_ID);
  const focusWasInside = Boolean(
    (layer || wall)?.contains?.(document.activeElement),
  );
  for (const node of [layer, wall]) {
    if (node && node.parentNode) node.parentNode.removeChild(node);
  }
  const target = returnFocusTo;
  returnFocusTo = null;
  /* Only when the wall held the focus: closing it must not steal the focus
     from wherever the player has since moved it (the store it opened). A
     button inside a sheet that has closed since simply does not take it. */
  if (focusWasInside && target?.isConnected && !target.disabled) {
    try { target.focus({ preventScroll: true }); } catch (_) { target.focus(); }
  }
}

/** Every control the Tab key may land on, in order. */
function focusables(root) {
  return [...root.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')]
    .filter((node) => !node.disabled && node.getClientRects().length > 0);
}

function element(tag, className, textContent) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (textContent) node.textContent = textContent;
  return node;
}


/**
 * Does this server refusal mean "your moon is too dark"?
 *
 * Only the PER-PLAYER game-start refusal does. A global cap is an operator
 * condition — offering that player a Refill would sell them something we
 * cannot deliver. A manga-only refusal is not a wall at all: panels degrade
 * and the duel goes ahead.
 */
function shouldRaiseMoonlightWall(error) {
  if (!error || typeof error !== 'object') return false;
  return error.code === 'monthly_usage_cap_exceeded'
    && error.scope === 'user'
    && error.kind === 'game_start';
}

/**
 * Exactly one action, chosen by who is looking. The Refill is the Manga Pass's
 * (the owner, 2026-09-25): everyone else — a free player, a Storyteller
 * holder, an unknown tier — is offered the pass above, because the checkout
 * route refuses them a refill and offering one would end in a 409 the player
 * did not earn.
 */
function resolveMoonlightOffer(status) {
  /* ON HOLD FOR A DISPUTED PAYMENT: purchases are refused until the bank
     decides, so the wall explains the hold and sells nothing. */
  if (status?.paymentDisputeOnHold === true) return null;
  const tier = String(status?.subscriptionType || '');
  if (tier !== 'moonfall_manga') return 'upgrade';
  return status?.refillAvailable === false ? null : 'refill';
}

/**
 * @typedef {Object} MoonlightWallCopy
 * @property {string} [title]
 * @property {string} [reason]
 * @property {string} [returns]
 * @property {string} [finishes]
 * @property {string} [refillNow]
 * @property {string} [refillOnce]
 * @property {string} [upgrade]
 * @property {string} [close]
 * @property {string} [closeLabel]
 *
 * @typedef {Object} MoonlightWallOptions
 * @property {Element|null} [anchor]
 * @property {{ fraction?: number, subscriptionType?: string, refillAvailable?: boolean }|null} [status]
 * @property {MoonlightWallCopy} [copy]
 * @property {'refill'|'upgrade'|null} [offer]
 * @property {(offer: string) => void} [onAction]
 * @property {() => void} [onOpenStore]
 *
 * @param {MoonlightWallOptions} [options]
 */
function openMoonlightWall({
  anchor,
  status,
  copy = {},
  offer = null,
  onAction,
  // Present so a caller CANNOT accidentally auto-open the store: it is only
  // ever invoked from the action the player presses.
  onOpenStore,
} = {}) {
  removeExisting();

  const layer = element('div', 'moonlight-wall-layer');
  layer.id = MOONLIGHT_WALL_LAYER_ID;
  const sheet = element('div', 'moonlight-wall');
  sheet.id = MOONLIGHT_WALL_ID;
  sheet.setAttribute('role', 'dialog');
  sheet.setAttribute('aria-modal', 'true');
  sheet.setAttribute('aria-labelledby', `${MOONLIGHT_WALL_ID}-title`);
  sheet.tabIndex = -1;

  // The game's crescent, dimmed: "the moon is low, not missing".
  const moon = element('div', 'moonlight-wall__moon');
  moon.setAttribute('aria-hidden', 'true');
  sheet.appendChild(moon);

  const heading = element('h2', 'moonlight-wall__title', copy.title || '');
  heading.id = `${MOONLIGHT_WALL_ID}-title`;
  sheet.appendChild(heading);
  /* WHY this moon is dark, when there is a reason the player could not
     guess: this billing period's refill is already used, or the mailbox
     already had its welcome on another account. */
  if (copy.reason) sheet.appendChild(element('p', 'moonlight-wall__reason', copy.reason));
  if (copy.returns) sheet.appendChild(element('p', 'moonlight-wall__returns', copy.returns));
  // The rule a player feels cheated over is stated in plain present tense, as
  // a standing property of the game — no number, no mention of a reserve.
  if (copy.finishes) sheet.appendChild(element('p', 'moonlight-wall__finishes', copy.finishes));

  if (offer) {
    const action = element('button', 'moonlight-wall__action');
    action.type = 'button';
    action.setAttribute('data-moonlight-action', offer);
    action.textContent = offer === 'refill'
      ? (copy.refillNow || '')
      : (copy.upgrade || copy.refillNow || '');
    action.addEventListener('click', () => {
      /* The wall has done its job once the player acts, and it gets out of
         the way first: what the action opens (the store, a checkout's
         confirmation) must be the one dialog on screen, and the store will
         not open over another modal one. */
      removeExisting();
      if (typeof onAction === 'function') onAction(offer);
      if (offer === 'upgrade' && typeof onOpenStore === 'function') onOpenStore();
    });
    sheet.appendChild(action);

    // Under the button, not in a tooltip. Stripe's own hosted page will say
    // the same thing, which is corroboration from a party that is not us.
    if (offer === 'refill' && copy.refillOnce) {
      sheet.appendChild(element('p', 'moonlight-wall__note', copy.refillOnce));
    }
  }

  const close = element('button', 'moonlight-wall__close', copy.close || '×');
  close.type = 'button';
  close.setAttribute('data-moonlight-close', '');
  close.setAttribute('aria-label', copy.closeLabel || 'Close');
  close.addEventListener('click', removeExisting);
  sheet.appendChild(close);

  /* Its own layer on <body>, whatever holds the button. The anchor is only
     where the keyboard goes back to: with no anchor it still opens — a
     missing element must never swallow the explanation the player is owed. */
  layer.appendChild(sheet);
  layer.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      removeExisting();
      return;
    }
    if (event.key !== 'Tab') return;
    const nodes = focusables(sheet);
    if (!nodes.length) {
      event.preventDefault();
      return;
    }
    // WebKit on macOS may skip native button tab stops. Own every step,
    // including the first one from the dialog, so focus cannot escape.
    event.preventDefault();
    event.stopPropagation();
    const current = nodes.findIndex((node) => node === document.activeElement);
    const next = event.shiftKey
      ? (current <= 0 ? nodes.length - 1 : current - 1)
      : (current + 1) % nodes.length;
    nodes[next].focus({ preventScroll: true });
  });
  // A press on the dim around the sheet dismisses it; one inside it stays.
  layer.addEventListener('click', (event) => {
    if (event.target === layer) removeExisting();
  });
  document.body.appendChild(layer);
  returnFocusTo = anchor || null;
  try { sheet.focus({ preventScroll: true }); } catch (_) { sheet.focus(); }
  return sheet;
}

export {
  MOONLIGHT_WALL_ID,
  MOONLIGHT_WALL_LAYER_ID,
  openMoonlightWall,
  removeExisting as closeMoonlightWall,
  resolveMoonlightOffer,
  shouldRaiseMoonlightWall,
};
